import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CreditCard,
  Search,
  Plus,
  Edit,
  Trash2,
  Check,
  X,
  Clock,
  Star,
  Crown,
  Zap,
  Package,
  DollarSign,
  Users,
  Infinity,
  Building2,
  MessageSquare,
  BarChart3,
  ClipboardList,
  Shield,
  Image,
  Bell,
  FileText,
  Share2,
  Loader,
  ToggleRight,
  Pause,
  Play,
  RefreshCw,
  History,
  AlertTriangle
} from 'lucide-react';
import { formatDate, formatPrice } from '../utils/helpers';
import { subscriptionsService } from '../services';
import featuresService from '../services/featuresService';
import usersService from '../services/usersService';
import { useToast } from '../context/ToastContext';
import './SubscriptionsPage.css';

const SubscriptionsPage = () => {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const isRTL = i18n.language === 'ar';
  const [activeTab, setActiveTab] = useState('subscriptions');
  const [searchQuery, setSearchQuery] = useState('');
  const [planSearchQuery, setPlanSearchQuery] = useState('');
  const [featuresSearchQuery, setFeaturesSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showUserModal, setShowUserModal] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isEditingPlan, setIsEditingPlan] = useState(false);
  
  // Assign plan state
  const [assignPlanData, setAssignPlanData] = useState({
    userId: '',
    userSearch: '',
    planId: '',
    duration: null,
    notes: ''
  });
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  
  // Action modals state
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState(''); // 'suspend', 'cancel', 'activate', 'change'
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [actionReason, setActionReason] = useState('');
  const [newPlanId, setNewPlanId] = useState('');
  const [newDuration, setNewDuration] = useState('');
  
  // Logs modal
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [planModalError, setPlanModalError] = useState('');
  const [isPlanDirty, setIsPlanDirty] = useState(false);
  const isPlanDirtyRef = React.useRef(false);

  // API state
  const [plans, setPlans] = useState([]);
  const [userSubscriptions, setUserSubscriptions] = useState([]);
  const [availableDynamicFeatures, setAvailableDynamicFeatures] = useState([]);
  const [stats, setStats] = useState({
    totalSubscribers: 0,
    activeSubscribers: 0,
    pendingPayments: 0,
    totalRevenue: 0
  });
  const [loading, setLoading] = useState(true);
  const [plansLoading, setPlansLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, total: 1, count: 0 });

  // Fetch plans from API
  const fetchPlans = useCallback(async () => {
    try {
      setPlansLoading(true);
      const response = await subscriptionsService.getAllPlans();
      if (response.success) {
        setPlans(response.data.plans || []);
      }
    } catch (err) {
      console.error('Error fetching plans:', err);
    } finally {
      setPlansLoading(false);
    }
  }, []);

  // Fetch dynamic features
  const fetchDynamicFeatures = useCallback(async () => {
    try {
      const response = await featuresService.getAll();
      if (response.success) {
        setAvailableDynamicFeatures(response.data.features || []);
      }
    } catch (err) {
      console.error('Error fetching dynamic features:', err);
    }
  }, []);

  // Fetch subscriptions from API
  const fetchSubscriptions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: pagination.current,
        limit: 20
      };

      if (searchQuery) {
        params.search = searchQuery;
      }

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      const response = await subscriptionsService.getAllSubscriptions(params);

      if (response.success) {
        setUserSubscriptions(response.data.subscriptions || []);
        setStats(response.data.stats || {
          totalSubscribers: 0,
          activeSubscribers: 0,
          pendingPayments: 0,
          totalRevenue: 0
        });
        setPagination(response.data.pagination || { current: 1, total: 1, count: 0 });
      } else {
        setError(response.message || t('errors.fetchFailed'));
      }
    } catch (err) {
      setError(err.message || t('errors.fetchFailed'));
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, statusFilter, pagination.current, t]);

  useEffect(() => {
    fetchPlans();
    fetchDynamicFeatures();
  }, [fetchPlans, fetchDynamicFeatures]);

  useEffect(() => {
    if (activeTab === 'subscriptions') {
      fetchSubscriptions();
    }
  }, [activeTab, fetchSubscriptions]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination(prev => ({ ...prev, current: 1 }));
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const getIconComponent = (iconName) => {
    const icons = { Package, Star, Crown, Zap };
    return icons[iconName] || Package;
  };

  const filteredSubscriptions = useMemo(() => {
    return userSubscriptions;
  }, [userSubscriptions]);

  // Filter plans based on search query
  const filteredPlans = useMemo(() => {
    if (!planSearchQuery.trim()) return plans;
    
    const query = planSearchQuery.toLowerCase();
    return plans.filter(plan => {
      const nameAr = (plan.nameAr || plan.name || '').toLowerCase();
      const nameEn = (plan.nameEn || '').toLowerCase();
      const descAr = (plan.descriptionAr || plan.description || '').toLowerCase();
      const descEn = (plan.descriptionEn || '').toLowerCase();
      const price = (plan.price || 0).toString();
      
      return nameAr.includes(query) || 
             nameEn.includes(query) || 
             descAr.includes(query) || 
             descEn.includes(query) ||
             price.includes(query);
    });
  }, [plans, planSearchQuery]);

  // eslint-disable-next-line no-unused-vars
  const handleActivateSubscription = useCallback(async (subscription) => {
    try {
      const response = await subscriptionsService.activateSubscription(subscription._id || subscription.id);
      if (response.success) {
        fetchSubscriptions();
        toast.success(t('subscriptions.activatedSuccess', 'تم تفعيل الاشتراك بنجاح'));
      } else {
        toast.error(response.message || t('errors.updateFailed'));
      }
    } catch (err) {
      toast.error(err.message || t('errors.updateFailed'));
    }
  }, [fetchSubscriptions, t, toast]);

  // eslint-disable-next-line no-unused-vars
  const handleCancelSubscription = useCallback(async (subscription) => {
    if (window.confirm(t('subscriptions.confirmCancel'))) {
      try {
        const response = await subscriptionsService.cancelSubscription(subscription._id || subscription.id);
        if (response.success) {
          fetchSubscriptions();
          toast.success(t('subscriptions.cancelledSuccess', 'تم إلغاء الاشتراك'));
        } else {
          toast.error(response.message || t('errors.updateFailed'));
        }
      } catch (err) {
        toast.error(err.message || t('errors.updateFailed'));
      }
    }
  }, [fetchSubscriptions, t, toast]);

  // Delete subscription permanently
  const handleDeleteSubscription = useCallback(async (subscription) => {
    if (window.confirm(t('subscriptions.confirmDelete', 'هل أنت متأكد من حذف هذا الاشتراك نهائياً؟ لا يمكن التراجع عن هذا الإجراء.'))) {
      try {
        const response = await subscriptionsService.deleteSubscription(subscription._id || subscription.id);
        if (response.success) {
          fetchSubscriptions();
          toast.success(t('subscriptions.deletedSuccess', 'تم حذف الاشتراك بنجاح'));
        } else {
          toast.error(response.message || t('errors.deleteFailed'));
        }
      } catch (err) {
        toast.error(err.message || t('errors.deleteFailed'));
      }
    }
  }, [fetchSubscriptions, t, toast]);

  const handleEditPlan = (plan) => {
    // إذا الاسم محفوظ في name وليس nameAr، نملأ nameAr منه تلقائياً
    setSelectedPlan({
      ...plan,
      nameAr: plan.nameAr || plan.name || '',
      nameEn: plan.nameEn || ''
    });
    setIsEditingPlan(true);
    setFeaturesSearchQuery('');
    setPlanModalError('');
    setIsPlanDirty(false);
    isPlanDirtyRef.current = false;
    setShowPlanModal(true);
  };

  const handleAddPlan = () => {
    setFeaturesSearchQuery('');
    setSelectedPlan({
      nameAr: '',
      nameEn: '',
      descriptionAr: '',
      descriptionEn: '',
      price: 0,
      duration: 30,
      color: '#3b82f6',
      icon: 'Star',
      features: {
        offersLimit: 5,
        requestsLimit: 10,
        catalogsLimit: 1,
        chatEnabled: false,
        reportsEnabled: false,
        supportType: 'basic',
        videoUploadEnabled: false,
        aiAnalysisEnabled: false
      },
      dynamicFeatures: [],
      isActive: true,
      isDefault: false
    });
    setIsEditingPlan(false);
    setPlanModalError('');
    setIsPlanDirty(false);
    isPlanDirtyRef.current = false;
    setShowPlanModal(true);
  };

  // Wrapper: any change to the plan form marks it as dirty
  const updateSelectedPlan = (updater) => {
    setSelectedPlan(typeof updater === 'function' ? updater : updater);
    setIsPlanDirty(true);
    isPlanDirtyRef.current = true;
  };

  const closePlanModal = () => {
    if (isPlanDirtyRef.current) {
      const confirmed = window.confirm('لديك تعديلات لم تُحفظ. هل تريد الخروج بدون حفظ؟');
      if (!confirmed) return;
    }
    setShowPlanModal(false);
    setFeaturesSearchQuery('');
    setPlanModalError('');
    setIsPlanDirty(false);
    isPlanDirtyRef.current = false;
  };

  const handleSavePlan = useCallback(async () => {
    // Validate required fields
    if (!selectedPlan?.nameAr?.trim()) {
      setPlanModalError('⚠️ يرجى إدخال اسم الخطة بالعربية');
      return;
    }
    if (selectedPlan?.price === '' || selectedPlan?.price === null || selectedPlan?.price === undefined) {
      setPlanModalError('⚠️ يرجى إدخال سعر الخطة');
      return;
    }
    setPlanModalError('');

    try {
      let response;
      if (isEditingPlan) {
        response = await subscriptionsService.updatePlan(selectedPlan._id || selectedPlan.id, selectedPlan);
      } else {
        response = await subscriptionsService.createPlan(selectedPlan);
      }
      
      if (response.success) {
        fetchPlans();
        setShowPlanModal(false);
        setSelectedPlan(null);
        setFeaturesSearchQuery('');
        setIsPlanDirty(false);
        isPlanDirtyRef.current = false;
        
        // Show success message with info about cancelled subscriptions if any
        let successMessage = isEditingPlan ? t('subscriptions.planUpdated', 'تم تحديث الخطة') : t('subscriptions.planCreated', 'تم إنشاء الخطة');
        if (response.data?.cancelledSubscriptionsCount > 0) {
          successMessage += ` (تم إلغاء ${response.data.cancelledSubscriptionsCount} اشتراك)`;
        }
        toast.success(successMessage);
      } else {
        toast.error(response.message || t('errors.saveFailed'));
      }
    } catch (err) {
      toast.error(err.message || t('errors.saveFailed'));
    }
  }, [isEditingPlan, selectedPlan, fetchPlans, t, toast]);

  const handleDeletePlan = useCallback(async (planId) => {
    const plan = plans.find(p => (p._id || p.id) === planId);
    const hasSubscriptions = plan?.subscribersCount > 0;
    
    let confirmMessage = t('subscriptions.confirmDeletePlan');
    if (hasSubscriptions) {
      confirmMessage = t('subscriptions.confirmDeletePlanWithSubscriptions', 
        `هل تريد حذف هذه الخطة؟ سيتم إلغاء ${plan.subscribersCount} اشتراك نشط.`);
    }
    
    if (window.confirm(confirmMessage)) {
      try {
        const forceDelete = hasSubscriptions ? '?force=true' : '';
        const response = await subscriptionsService.deletePlan(planId, forceDelete);
        if (response.success) {
          fetchPlans();
          let successMessage = t('subscriptions.planDeleted', 'تم حذف الخطة');
          if (response.data?.cancelledSubscriptionsCount > 0) {
            successMessage += ` (تم إلغاء ${response.data.cancelledSubscriptionsCount} اشتراك)`;
          }
          toast.success(successMessage);
        } else {
          toast.error(response.message || t('errors.deleteFailed'));
        }
      } catch (err) {
        toast.error(err.message || t('errors.deleteFailed'));
      }
    }
  }, [fetchPlans, t, toast, plans]);

  // Handle set plan as default
  const handleSetDefaultPlan = useCallback(async (planId) => {
    if (window.confirm(t('subscriptions.confirmSetDefault', 'هل تريد تعيين هذه الخطة كافتراضية؟ سيحصل عليها جميع المستخدمين الجدد تلقائياً.'))) {
      try {
        const response = await subscriptionsService.setDefaultPlan(planId);
        if (response.success) {
          fetchPlans();
          toast.success(t('subscriptions.defaultPlanSet', 'تم تعيين الخطة كافتراضية'));
        } else {
          toast.error(response.message || t('errors.saveFailed'));
        }
      } catch (err) {
        toast.error(err.message || t('errors.saveFailed'));
      }
    }
  }, [fetchPlans, t, toast]);

  // Handle assign plan to all users
  const handleAssignPlanToAll = useCallback(async (planId) => {
    const plan = plans.find(p => (p._id || p.id) === planId);
    const overwrite = window.confirm(
      t('subscriptions.confirmAssignToAll', 
        `هل تريد تعيين خطة "${plan?.nameAr}" لجميع المستخدمين؟\n\nاضغط "موافق" لاستبدال الاشتراكات الموجودة\nاضغط "إلغاء" للتعيين فقط للمستخدمين بدون اشتراك نشط`)
    );
    
    try {
      const response = await subscriptionsService.assignPlanToAll(planId, { overwriteExisting: overwrite });
      if (response.success) {
        fetchPlans();
        fetchSubscriptions();
        const stats = response.data?.stats || {};
        toast.success(
          t('subscriptions.planAssignedToAll', 
            `تم تعيين الخطة بنجاح - تم تعيين: ${stats.assignedCount || 0}، تم تخطي: ${stats.skippedCount || 0}، تم إلغاء: ${stats.cancelledCount || 0}`)
        );
      } else {
        toast.error(response.message || t('errors.saveFailed'));
      }
    } catch (err) {
      toast.error(err.message || t('errors.saveFailed'));
    }
  }, [fetchPlans, fetchSubscriptions, t, toast, plans]);

  // Fetch users for assign plan modal
  const fetchUsers = useCallback(async (search = '') => {
    try {
      setUsersLoading(true);
      const response = await usersService.getAll({ search, limit: 50 });
      if (response.success) {
        setUsers(response.data.users || []);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  // Handle assign plan to user
  const handleAssignPlan = useCallback(async () => {
    if (!assignPlanData.userId || !assignPlanData.planId) {
      toast.error(t('subscriptions.selectUserAndPlan', 'الرجاء اختيار مستخدم وخطة'));
      return;
    }

    try {
      const response = await subscriptionsService.assignPlanToUser({
        userId: assignPlanData.userId,
        planId: assignPlanData.planId,
        duration: assignPlanData.duration
      });
      
      if (response.success) {
        fetchSubscriptions();
        setShowUserModal(false);
        setAssignPlanData({ userId: '', userSearch: '', planId: '', duration: null });
        toast.success(t('subscriptions.planAssigned', 'تم تعيين الخطة للمستخدم'));
      } else {
        toast.error(response.message || t('errors.saveFailed'));
      }
    } catch (err) {
      toast.error(err.message || t('errors.saveFailed'));
    }
  }, [assignPlanData, fetchSubscriptions, t, toast]);

  // Subscription Action Handlers
  const openActionModal = (subscription, action) => {
    setSelectedSubscription(subscription);
    setActionType(action);
    setActionReason('');
    setNewPlanId('');
    setNewDuration('');
    setShowActionModal(true);
  };

  const handleSubscriptionAction = useCallback(async () => {
    if (!selectedSubscription) return;
    
    try {
      let response;
      const subscriptionId = selectedSubscription._id || selectedSubscription.id;
      
      switch (actionType) {
        case 'suspend':
          response = await subscriptionsService.suspendSubscription(subscriptionId, { reason: actionReason });
          break;
        case 'cancel':
          response = await subscriptionsService.cancelSubscription(subscriptionId, { reason: actionReason });
          break;
        case 'activate':
          response = await subscriptionsService.activateSubscription(subscriptionId, { 
            duration: newDuration ? Number(newDuration) : undefined,
            reason: actionReason 
          });
          break;
        case 'change':
          if (!newPlanId) {
            toast.error(t('subscriptions.selectNewPlan', 'الرجاء اختيار خطة جديدة'));
            return;
          }
          response = await subscriptionsService.changePlan(subscriptionId, { 
            planId: newPlanId,
            duration: newDuration ? Number(newDuration) : undefined,
            reason: actionReason 
          });
          break;
        default:
          return;
      }
      
      if (response.success) {
        fetchSubscriptions();
        setShowActionModal(false);
        setSelectedSubscription(null);
        const successMessages = {
          suspend: t('subscriptions.suspended', 'تم إيقاف الاشتراك'),
          cancel: t('subscriptions.cancelled', 'تم إلغاء الاشتراك'),
          activate: t('subscriptions.activated', 'تم تفعيل الاشتراك'),
          change: t('subscriptions.planChanged', 'تم تغيير الخطة')
        };
        toast.success(successMessages[actionType]);
      } else {
        toast.error(response.message || t('errors.actionFailed'));
      }
    } catch (err) {
      toast.error(err.message || t('errors.actionFailed'));
    }
  }, [selectedSubscription, actionType, actionReason, newPlanId, newDuration, fetchSubscriptions, t, toast]);

  // Fetch subscription logs
  const fetchSubscriptionLogs = useCallback(async (subscriptionId) => {
    try {
      setLogsLoading(true);
      const response = await subscriptionsService.getSubscriptionLogs(subscriptionId);
      if (response.success) {
        setLogs(response.data.logs || []);
      }
    } catch (err) {
      console.error('Error fetching logs:', err);
    } finally {
      setLogsLoading(false);
    }
  }, []);

  const openLogsModal = (subscription) => {
    setSelectedSubscription(subscription);
    setShowLogsModal(true);
    fetchSubscriptionLogs(subscription._id || subscription.id);
  };

  // Status badge helper
  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: '#22c55e', bg: '#dcfce7', label: t('subscriptions.active', 'نشط') },
      expired: { color: '#f59e0b', bg: '#fef3c7', label: t('subscriptions.expired', 'منتهي') },
      cancelled: { color: '#ef4444', bg: '#fee2e2', label: t('subscriptions.cancelled', 'ملغي') },
      suspended: { color: '#6366f1', bg: '#e0e7ff', label: t('subscriptions.suspended', 'موقوف') },
      pending_payment: { color: '#f97316', bg: '#ffedd5', label: t('subscriptions.pendingPayment', 'في انتظار الدفع') },
      trial: { color: '#8b5cf6', bg: '#f3e8ff', label: t('subscriptions.trial', 'تجربة') }
    };
    const config = statusConfig[status] || statusConfig.active;
    return (
      <span className="status-badge" style={{ background: config.bg, color: config.color }}>
        {config.label}
      </span>
    );
  };

  const renderFeatureValue = (feature) => {
    if (feature.type === 'boolean') {
      return feature.value ? (
        <Check size={16} className="feature-check" />
      ) : (
        <X size={16} className="feature-x" />
      );
    }
    if (feature.type === 'number') {
      return feature.value === -1 ? (
        <Infinity size={16} className="feature-infinity" />
      ) : (
        <span>{feature.value}</span>
      );
    }
    return <span>{feature.value}</span>;
  };

  // تحويل features object إلى array للعرض
  const getPlanFeaturesArray = (plan) => {
    if (!plan || !plan.features) return [];
    
    const features = plan.features;
    const arr = [];
    
    if (features.offersLimit !== undefined) {
      arr.push({ name: isRTL ? 'عدد العروض' : 'Offers', type: 'number', value: features.offersLimit });
    }
    if (features.requestsLimit !== undefined) {
      arr.push({ name: isRTL ? 'الطلبات' : 'Requests', type: 'number', value: features.requestsLimit });
    }
    if (features.catalogsLimit !== undefined) {
      arr.push({ name: isRTL ? 'الكتالوجات' : 'Catalogs', type: 'number', value: features.catalogsLimit });
    }
    if (features.chatEnabled !== undefined) {
      arr.push({ name: isRTL ? 'المحادثات' : 'Chat', type: 'boolean', value: features.chatEnabled });
    }
    if (features.reportsEnabled !== undefined) {
      arr.push({ name: isRTL ? 'التقارير' : 'Reports', type: 'boolean', value: features.reportsEnabled });
    }
    if (features.videoUploadEnabled !== undefined) {
      arr.push({ name: isRTL ? 'رفع الفيديو' : 'Video Upload', type: 'boolean', value: features.videoUploadEnabled });
    }
    if (features.aiAnalysisEnabled !== undefined) {
      arr.push({ name: isRTL ? 'تحليل AI' : 'AI Analysis', type: 'boolean', value: features.aiAnalysisEnabled });
    }
    if (features.supportType) {
      arr.push({ name: isRTL ? 'الدعم الفني' : 'Support', type: 'text', value: features.supportType });
    }
    
    return arr;
  };

  const getPlanById = (planId) => {
    if (!planId) return null;
    if (typeof planId === 'object') return planId;
    return plans.find(p => (p._id || p.id) === planId) || null;
  };

  // Feature codes that are too basic to show in the plan editor
  const HIDDEN_FEATURE_CODES = new Set([
    'PAGE_DASHBOARD', 'PAGE_OFFERS', 'PAGE_REQUESTS', 'PAGE_SETTINGS',
    'PAGE_NOTIFICATIONS', 'PAGE_INVOICES', 'PAGE_FAQ', 'PAGE_TUTORIALS', 'PAGE_SUBSCRIPTION',
    'ACTION_ADD_OFFER', 'ACTION_EDIT_OFFER', 'ACTION_DELETE_OFFER',
    'ACTION_ADD_REQUEST', 'ACTION_EDIT_REQUEST', 'ACTION_DELETE_REQUEST',
    'ACTION_MATCHING', 'ACTION_START_CHAT', 'ACTION_SEND_MESSAGE'
  ]);

  // Filter dynamic features based on search query, excluding basic/hidden codes
  const filteredDynamicFeatures = useMemo(() => {
    const visible = availableDynamicFeatures.filter(f => !HIDDEN_FEATURE_CODES.has(f.code));
    if (!featuresSearchQuery.trim()) return visible;

    const query = featuresSearchQuery.toLowerCase();
    return visible.filter(feature => {
      const nameAr = (feature.nameAr || '').toLowerCase();
      const nameEn = (feature.nameEn || '').toLowerCase();
      const descAr = (feature.descriptionAr || '').toLowerCase();
      const descEn = (feature.descriptionEn || '').toLowerCase();
      const category = (feature.category || '').toLowerCase();

      return nameAr.includes(query) ||
             nameEn.includes(query) ||
             descAr.includes(query) ||
             descEn.includes(query) ||
             category.includes(query);
    });
  }, [availableDynamicFeatures, featuresSearchQuery]);

  return (
    <div className="subscriptions-page">
      <div className="page-header">
        <div className="header-title">
          <CreditCard size={28} />
          <h1>{t('subscriptions.title')}</h1>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)' }}>
            <Users size={24} />
          </div>
          <div className="stat-info">
            <span className="stat-value">{stats.totalSubscribers}</span>
            <span className="stat-label">{t('subscriptions.totalSubscribers')}</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)' }}>
            <Check size={24} />
          </div>
          <div className="stat-info">
            <span className="stat-value">{stats.activeSubscribers}</span>
            <span className="stat-label">{t('subscriptions.activeSubscribers')}</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)' }}>
            <Clock size={24} />
          </div>
          <div className="stat-info">
            <span className="stat-value">{stats.pendingPayments}</span>
            <span className="stat-label">{t('subscriptions.pendingPayments')}</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)' }}>
            <DollarSign size={24} />
          </div>
          <div className="stat-info">
            <span className="stat-value">{formatPrice(stats.totalRevenue)}</span>
            <span className="stat-label">{t('subscriptions.totalRevenue')}</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="page-tabs">
        <button
          className={`tab-btn ${activeTab === 'subscriptions' ? 'active' : ''}`}
          onClick={() => setActiveTab('subscriptions')}
        >
          <Users size={18} />
          {t('subscriptions.userSubscriptions')}
        </button>
        <button
          className={`tab-btn ${activeTab === 'plans' ? 'active' : ''}`}
          onClick={() => setActiveTab('plans')}
        >
          <Package size={18} />
          {t('subscriptions.plans')}
        </button>
      </div>

      {/* User Subscriptions Tab */}
      {activeTab === 'subscriptions' && (
        <div className="subscriptions-section">
          <div className="section-header">
            <div className="search-box">
              <Search size={20} />
              <input
                type="text"
                placeholder={t('subscriptions.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <button className="btn btn-primary" onClick={() => setShowUserModal(true)}>
              <Plus size={18} />
              {t('subscriptions.assignPlan', 'تعيين خطة لمستخدم')}
            </button>
          </div>
          <div className="filter-tabs">
            <button
              className={`filter-tab ${statusFilter === 'all' ? 'active' : ''}`}
              onClick={() => setStatusFilter('all')}
            >
              {t('common.all')}
            </button>
            <button
              className={`filter-tab ${statusFilter === 'active' ? 'active' : ''}`}
              onClick={() => setStatusFilter('active')}
            >
              {t('subscriptions.active')}
            </button>
            <button
              className={`filter-tab ${statusFilter === 'pending' ? 'active' : ''}`}
              onClick={() => setStatusFilter('pending')}
            >
              {t('subscriptions.pending')}
            </button>
            <button
              className={`filter-tab ${statusFilter === 'expired' ? 'active' : ''}`}
              onClick={() => setStatusFilter('expired')}
            >
              {t('subscriptions.expired')}
            </button>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="loading-state">
              <Loader size={32} className="spin" />
              <p>{t('common.loading')}</p>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="error-state">
              <p>{error}</p>
              <button className="btn btn-primary" onClick={fetchSubscriptions}>
                {t('common.retry')}
              </button>
            </div>
          )}

          {/* Subscriptions Table */}
          {!loading && !error && (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('subscriptions.user')}</th>
                    <th>{t('subscriptions.plan')}</th>
                    <th>{t('subscriptions.startDate')}</th>
                    <th>{t('subscriptions.endDate')}</th>
                    <th>{t('subscriptions.amount')}</th>
                    <th>{t('common.status')}</th>
                    <th>{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubscriptions.map((sub) => {
                    const plan = getPlanById(sub.plan || sub.planId || sub.plan?._id);
                    const planName = sub.planName || plan?.nameAr || plan?.nameEn || plan?.name || '-';
                    return (
                      <tr key={sub._id || sub.id}>
                        <td>
                          <div className="user-cell">
                            <div className="user-avatar">
                              {(sub.userName || sub.user?.name || '?').charAt(0)}
                            </div>
                            <div className="user-info">
                              <span className="user-name">{sub.userName || sub.user?.name}</span>
                              <span className="user-email">{sub.userEmail || sub.user?.email}</span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="plan-badge" style={{ 
                            background: (plan?.color || '#3b82f6') + '20',
                            color: plan?.color || '#3b82f6'
                          }}>
                            {planName}
                          </span>
                        </td>
                        <td>{formatDate(sub.startDate)}</td>
                        <td>{sub.endDate ? formatDate(sub.endDate) : '-'}</td>
                        <td className="amount-cell">{formatPrice(sub.amount || 0)}</td>
                        <td>{getStatusBadge(sub.status)}</td>
                        <td>
                          <div className="action-buttons">
                            {/* Activate button - for suspended/cancelled/expired */}
                            {['suspended', 'cancelled', 'expired', 'pending_payment'].includes(sub.status) && (
                              <button 
                                className="action-btn success"
                                onClick={() => openActionModal(sub, 'activate')}
                                title={t('subscriptions.activate', 'تفعيل')}
                              >
                                <Play size={18} />
                              </button>
                            )}
                            {/* Suspend button - for active/trial */}
                            {['active', 'trial'].includes(sub.status) && (
                              <button 
                                className="action-btn warning"
                                onClick={() => openActionModal(sub, 'suspend')}
                                title={t('subscriptions.suspend', 'إيقاف مؤقت')}
                              >
                                <Pause size={18} />
                              </button>
                            )}
                            {/* Cancel button - for active/trial/suspended */}
                            {['active', 'trial', 'suspended'].includes(sub.status) && (
                              <button 
                                className="action-btn warning"
                                onClick={() => openActionModal(sub, 'cancel')}
                                title={t('subscriptions.cancel', 'إلغاء')}
                              >
                                <X size={18} />
                              </button>
                            )}
                            {/* Change plan button */}
                            {['active', 'trial'].includes(sub.status) && (
                              <button 
                                className="action-btn primary"
                                onClick={() => openActionModal(sub, 'change')}
                                title={t('subscriptions.changePlan', 'تغيير الخطة')}
                              >
                                <RefreshCw size={18} />
                              </button>
                            )}
                            {/* View logs button */}
                            <button 
                              className="action-btn"
                              onClick={() => openLogsModal(sub)}
                              title={t('subscriptions.viewHistory', 'عرض السجل')}
                            >
                              <History size={18} />
                            </button>
                            {/* Delete button - always available */}
                            <button 
                              className="action-btn danger"
                              onClick={() => handleDeleteSubscription(sub)}
                              title={t('subscriptions.delete', 'حذف نهائي')}
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {!loading && !error && filteredSubscriptions.length === 0 && (
            <div className="empty-state">
              <Users size={48} />
              <h3>{t('subscriptions.noSubscriptions')}</h3>
              <p>{t('subscriptions.noSubscriptionsDescription')}</p>
            </div>
          )}

          {/* Pagination */}
          {!loading && !error && pagination.total > 1 && (
            <div className="pagination">
              <button
                className="btn btn-outline"
                disabled={pagination.current === 1}
                onClick={() => setPagination(prev => ({ ...prev, current: prev.current - 1 }))}
              >
                {t('common.previous')}
              </button>
              <span className="pagination-info">
                {t('common.page')} {pagination.current} {t('common.of')} {pagination.total}
              </span>
              <button
                className="btn btn-outline"
                disabled={pagination.current === pagination.total}
                onClick={() => setPagination(prev => ({ ...prev, current: prev.current + 1 }))}
              >
                {t('common.next')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Plans Tab */}
      {activeTab === 'plans' && (
        <div className="plans-section">
          <div className="section-header">
            <div className="search-box">
              <Search size={20} />
              <input
                type="text"
                placeholder={t('subscriptions.searchPlansPlaceholder', 'ابحث عن خطة...')}
                value={planSearchQuery}
                onChange={(e) => setPlanSearchQuery(e.target.value)}
              />
            </div>
            <button className="btn btn-primary" onClick={handleAddPlan}>
              <Plus size={18} />
              {t('subscriptions.addPlan')}
            </button>
          </div>

          {plansLoading && (
            <div className="loading-state">
              <Loader size={32} className="spin" />
              <p>{t('common.loading')}</p>
            </div>
          )}

          {!plansLoading && (
            <div className="plans-grid">
              {filteredPlans.map((plan) => {
                const IconComponent = getIconComponent(plan.icon);
                const planFeatures = getPlanFeaturesArray(plan);
                return (
                  <div key={plan._id || plan.id} className="plan-card" style={{ borderColor: plan.color }}>
                    <div className="plan-header" style={{ background: plan.color }}>
                      {plan.isDefault && (
                        <span className="default-badge">{t('subscriptions.default', 'افتراضي')}</span>
                      )}
                      <IconComponent size={32} />
                      <h3>{plan.nameAr || plan.nameEn || plan.name || '—'}</h3>
                      <span className="plan-price">
                        {plan.price === 0 ? t('subscriptions.free') : `${formatPrice(plan.price)} ${t('common.sar')}`}
                      </span>
                      {plan.duration > 0 && (
                        <span className="plan-duration">/ {plan.duration} {t('subscriptions.days')}</span>
                      )}
                      {Number(plan.duration) === 0 && (
                        <span className="plan-duration">/ {t('subscriptions.unlimited', 'لا نهائي')}</span>
                      )}
                    </div>
                    <div className="plan-features">
                      {planFeatures.length > 0 ? (
                        planFeatures.slice(0, 5).map((feature, idx) => (
                          <div key={idx} className="feature-row">
                            <span className="feature-name">{feature.name}</span>
                            <span className="feature-value">{renderFeatureValue(feature)}</span>
                          </div>
                        ))
                      ) : (
                        <div className="no-features">
                          <p>{t('subscriptions.noFeatures', 'لا توجد مميزات')}</p>
                        </div>
                      )}
                      {planFeatures.length > 5 && (
                        <div className="more-features-hint">
                          +{planFeatures.length - 5} {t('subscriptions.moreFeatures', 'مميزات أخرى')}
                        </div>
                      )}
                    </div>
                    <div className="plan-footer">
                      <span className="subscribers-count">
                        <Users size={14} />
                        {plan.subscribersCount || 0} {t('subscriptions.subscribers')}
                      </span>
                      <div className="plan-actions">
                        {!plan.isDefault && (
                          <button
                            className="action-btn success"
                            onClick={() => handleSetDefaultPlan(plan._id || plan.id)}
                            title={t('subscriptions.setAsDefault', 'تعيين كافتراضي')}
                          >
                            <Star size={16} />
                          </button>
                        )}
                        <button
                          className="action-btn info"
                          onClick={() => handleAssignPlanToAll(plan._id || plan.id)}
                          title={t('subscriptions.assignToAll', 'تعيين للجميع')}
                        >
                          <Users size={16} />
                        </button>
                        <button className="action-btn" onClick={() => handleEditPlan(plan)}>
                          <Edit size={16} />
                        </button>
                        <button className="action-btn danger" onClick={() => handleDeletePlan(plan._id || plan.id)}>
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {!plansLoading && filteredPlans.length === 0 && planSearchQuery && (
            <div className="empty-state">
              <Search size={48} />
              <h3>{t('subscriptions.noSearchResults', 'لا توجد نتائج بحث')}</h3>
              <p>{t('subscriptions.tryDifferentKeywords', 'جرب كلمات بحث مختلفة')}</p>
              <button className="btn btn-outline" onClick={() => setPlanSearchQuery('')}>
                <X size={18} />
                {t('common.clearSearch', 'مسح البحث')}
              </button>
            </div>
          )}

          {!plansLoading && plans.length === 0 && !planSearchQuery && (
            <div className="empty-state">
              <Package size={48} />
              <h3>{t('subscriptions.noPlans')}</h3>
              <p>{t('subscriptions.noPlansDescription')}</p>
              <button className="btn btn-primary" onClick={handleAddPlan}>
                <Plus size={18} />
                {t('subscriptions.addPlan')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Plan Modal */}
      {showPlanModal && selectedPlan && (
        <div className="modal-overlay">
          <div className="modal plan-modal">
            <div className="modal-header">
              <h2>{isEditingPlan ? t('subscriptions.editPlan') : t('subscriptions.addPlan')}</h2>
              <button className="modal-close" onClick={closePlanModal}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>{t('subscriptions.planNameAr')} <span className="required-star">*</span></label>
                  <input
                    type="text"
                    className={`form-input${planModalError && !selectedPlan.nameAr?.trim() ? ' input-error' : ''}`}
                    value={selectedPlan.nameAr || ''}
                    onChange={(e) => updateSelectedPlan({ ...selectedPlan, nameAr: e.target.value })}
                    placeholder="اسم الخطة بالعربية"
                  />
                </div>
                <div className="form-group">
                  <label>{t('subscriptions.planNameEn')}</label>
                  <input
                    type="text"
                    className="form-input"
                    value={selectedPlan.nameEn || ''}
                    onChange={(e) => updateSelectedPlan({ ...selectedPlan, nameEn: e.target.value })}
                    placeholder="Plan name in English"
                  />
                </div>
                <div className="form-group">
                  <label>{t('subscriptions.descriptionAr', 'الوصف بالعربية')}</label>
                  <textarea
                    className="form-input"
                    value={selectedPlan.descriptionAr || ''}
                    onChange={(e) => updateSelectedPlan({ ...selectedPlan, descriptionAr: e.target.value })}
                    rows={2}
                    placeholder="وصف الخطة بالعربية"
                  />
                </div>
                <div className="form-group">
                  <label>{t('subscriptions.descriptionEn', 'الوصف بالإنجليزية')}</label>
                  <textarea
                    className="form-input"
                    value={selectedPlan.descriptionEn || ''}
                    onChange={(e) => updateSelectedPlan({ ...selectedPlan, descriptionEn: e.target.value })}
                    rows={2}
                    placeholder="Plan description in English"
                  />
                </div>
                <div className="form-group">
                  <label>{t('subscriptions.price')} <span className="required-star">*</span></label>
                  <input
                    type="number"
                    className={`form-input${planModalError && (selectedPlan.price === '' || selectedPlan.price === null || selectedPlan.price === undefined) ? ' input-error' : ''}`}
                    value={selectedPlan.price ?? 0}
                    onChange={(e) => updateSelectedPlan({ ...selectedPlan, price: Number(e.target.value) })}
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label>{t('subscriptions.duration')}</label>
                  <div className="limit-input-row">
                    <input
                      type="number"
                      className="form-input"
                      value={selectedPlan.duration === 0 ? '' : (selectedPlan.duration ?? 30)}
                      disabled={selectedPlan.duration === 0}
                      onChange={(e) => updateSelectedPlan({ ...selectedPlan, duration: Number(e.target.value) })}
                      min="1"
                      placeholder="عدد الأيام"
                    />
                    <label className="unlimited-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedPlan.duration === 0}
                        onChange={(e) => updateSelectedPlan({
                          ...selectedPlan,
                          duration: e.target.checked ? 0 : 30
                        })}
                      />
                      <span>غير محدود</span>
                    </label>
                  </div>
                </div>
                <div className="form-group">
                  <label>{t('subscriptions.color')}</label>
                  <input
                    type="color"
                    className="form-input color-input"
                    value={selectedPlan.color || '#3b82f6'}
                    onChange={(e) => updateSelectedPlan({ ...selectedPlan, color: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>{t('subscriptions.icon')}</label>
                  <select
                    className="form-input"
                    value={selectedPlan.icon || 'Star'}
                    onChange={(e) => updateSelectedPlan({ ...selectedPlan, icon: e.target.value })}
                  >
                    <option value="Package">📦 صندوق</option>
                    <option value="Star">⭐ نجمة</option>
                    <option value="Crown">👑 تاج</option>
                    <option value="Zap">⚡ برق</option>
                  </select>
                </div>
              </div>

              {/* حدود الاستخدام */}
              <div className="features-section">
                <div className="features-section-header">
                  <div>
                    <h3>حدود الاستخدام</h3>
                  </div>
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <label>عدد العروض</label>
                    <div className="limit-input-row">
                      <input
                        type="number"
                        className="form-input"
                        value={selectedPlan.features?.offersLimit === -1 ? '' : (selectedPlan.features?.offersLimit ?? 5)}
                        disabled={selectedPlan.features?.offersLimit === -1}
                        onChange={(e) => updateSelectedPlan({
                          ...selectedPlan,
                          features: { ...selectedPlan.features, offersLimit: Number(e.target.value) }
                        })}
                        min="0"
                        placeholder="عدد العروض"
                      />
                      <label className="unlimited-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedPlan.features?.offersLimit === -1}
                          onChange={(e) => updateSelectedPlan({
                            ...selectedPlan,
                            features: { ...selectedPlan.features, offersLimit: e.target.checked ? -1 : 5 }
                          })}
                        />
                        <span>غير محدود</span>
                      </label>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>عدد الطلبات</label>
                    <div className="limit-input-row">
                      <input
                        type="number"
                        className="form-input"
                        value={selectedPlan.features?.requestsLimit === -1 ? '' : (selectedPlan.features?.requestsLimit ?? 10)}
                        disabled={selectedPlan.features?.requestsLimit === -1}
                        onChange={(e) => updateSelectedPlan({
                          ...selectedPlan,
                          features: { ...selectedPlan.features, requestsLimit: Number(e.target.value) }
                        })}
                        min="0"
                        placeholder="عدد الطلبات"
                      />
                      <label className="unlimited-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedPlan.features?.requestsLimit === -1}
                          onChange={(e) => updateSelectedPlan({
                            ...selectedPlan,
                            features: { ...selectedPlan.features, requestsLimit: e.target.checked ? -1 : 10 }
                          })}
                        />
                        <span>غير محدود</span>
                      </label>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>عدد الكتالوجات</label>
                    <div className="limit-input-row">
                      <input
                        type="number"
                        className="form-input"
                        value={selectedPlan.features?.catalogsLimit === -1 ? '' : (selectedPlan.features?.catalogsLimit ?? 1)}
                        disabled={selectedPlan.features?.catalogsLimit === -1}
                        onChange={(e) => updateSelectedPlan({
                          ...selectedPlan,
                          features: { ...selectedPlan.features, catalogsLimit: Number(e.target.value) }
                        })}
                        min="0"
                        placeholder="عدد الكتالوجات"
                      />
                      <label className="unlimited-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedPlan.features?.catalogsLimit === -1}
                          onChange={(e) => updateSelectedPlan({
                            ...selectedPlan,
                            features: { ...selectedPlan.features, catalogsLimit: e.target.checked ? -1 : 1 }
                          })}
                        />
                        <span>غير محدود</span>
                      </label>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>نوع الدعم</label>
                    <select
                      className="form-input"
                      value={selectedPlan.features?.supportType || 'basic'}
                      onChange={(e) => updateSelectedPlan({
                        ...selectedPlan,
                        features: { ...selectedPlan.features, supportType: e.target.value }
                      })}
                    >
                      <option value="basic">أساسي</option>
                      <option value="email">بريد إلكتروني</option>
                      <option value="priority">أولوية</option>
                      <option value="dedicated">مخصص</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={selectedPlan.features?.chatEnabled || false}
                        onChange={(e) => updateSelectedPlan({
                          ...selectedPlan,
                          features: { ...selectedPlan.features, chatEnabled: e.target.checked }
                        })}
                      />
                      المحادثات
                    </label>
                  </div>
                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={selectedPlan.features?.reportsEnabled || false}
                        onChange={(e) => updateSelectedPlan({
                          ...selectedPlan,
                          features: { ...selectedPlan.features, reportsEnabled: e.target.checked }
                        })}
                      />
                      التقارير
                    </label>
                  </div>
                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={selectedPlan.features?.videoUploadEnabled || false}
                        onChange={(e) => updateSelectedPlan({
                          ...selectedPlan,
                          features: { ...selectedPlan.features, videoUploadEnabled: e.target.checked }
                        })}
                      />
                      رفع الفيديو
                    </label>
                  </div>
                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={selectedPlan.features?.aiAnalysisEnabled || false}
                        onChange={(e) => updateSelectedPlan({
                          ...selectedPlan,
                          features: { ...selectedPlan.features, aiAnalysisEnabled: e.target.checked }
                        })}
                      />
                      تحليل AI
                    </label>
                  </div>
                </div>
              </div>

              {/* Dynamic Features Section */}
              <div className="features-section">
                <div className="features-section-header">
                  <div>
                    <ToggleRight size={18} />
                    <h3>{t('subscriptions.dynamicFeatures', 'الميزات الديناميكية')}</h3>
                  </div>
                  {availableDynamicFeatures.length > 0 && (
                    <div className="features-actions">
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        onClick={() => {
                          // Select All
                          const allFeatures = filteredDynamicFeatures.map(feature => ({
                            feature: feature._id,
                            value: feature.defaultValue
                          }));
                          updateSelectedPlan({
                            ...selectedPlan,
                            dynamicFeatures: [
                              ...(selectedPlan.dynamicFeatures || []).filter(
                                df => !filteredDynamicFeatures.some(f => f._id === (df.feature?._id || df.feature))
                              ),
                              ...allFeatures
                            ]
                          });
                        }}
                      >
                        {t('common.selectAll', 'تحديد الكل')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        onClick={() => {
                          // Deselect All filtered
                          updateSelectedPlan({
                            ...selectedPlan,
                            dynamicFeatures: (selectedPlan.dynamicFeatures || []).filter(
                              df => !filteredDynamicFeatures.some(f => f._id === (df.feature?._id || df.feature))
                            )
                          });
                        }}
                      >
                        {t('common.deselectAll', 'إلغاء تحديد الكل')}
                      </button>
                    </div>
                  )}
                </div>
                <p className="features-section-desc">{t('subscriptions.dynamicFeaturesDesc', 'حدد الميزات والصلاحيات المتاحة لهذه الخطة')}</p>
                
                {/* Search Features */}
                {availableDynamicFeatures.length > 0 && (
                  <div className="features-search">
                    <Search size={18} />
                    <input
                      type="text"
                      placeholder={t('subscriptions.searchFeatures', 'ابحث عن ميزة...')}
                      value={featuresSearchQuery}
                      onChange={(e) => setFeaturesSearchQuery(e.target.value)}
                    />
                    {featuresSearchQuery && (
                      <button
                        type="button"
                        className="clear-search-btn"
                        onClick={() => setFeaturesSearchQuery('')}
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                )}
                
                {availableDynamicFeatures.length === 0 ? (
                  <div className="no-features-notice">
                    <p>{t('subscriptions.noFeaturesAvailable', 'لا توجد ميزات متاحة. قم بإضافة ميزات من صفحة الميزات والصلاحيات أولاً.')}</p>
                  </div>
                ) : filteredDynamicFeatures.length === 0 ? (
                  <div className="no-features-notice">
                    <Search size={24} />
                    <p>{t('subscriptions.noFeaturesFound', 'لا توجد ميزات مطابقة للبحث')}</p>
                  </div>
                ) : (
                  <div className="dynamic-features-list">
                    {/* Group by category */}
                    {Object.entries(
                      filteredDynamicFeatures.reduce((acc, feature) => {
                        const cat = feature.category || 'other';
                        if (!acc[cat]) acc[cat] = [];
                        acc[cat].push(feature);
                        return acc;
                      }, {})
                    ).map(([category, features]) => (
                      <div key={category} className="feature-category-group">
                        <h4 className="feature-category-title">
                          {t(`features.categories.${category}`, category)}
                        </h4>
                        <div className="feature-items">
                          {features.map((feature) => {
                            const isSelected = selectedPlan.dynamicFeatures?.some(
                              df => (df.feature?._id || df.feature) === feature._id
                            );
                            const currentValue = selectedPlan.dynamicFeatures?.find(
                              df => (df.feature?._id || df.feature) === feature._id
                            )?.value;

                            return (
                              <div key={feature._id} className={`feature-item ${isSelected ? 'selected' : ''}`}>
                                <div className="feature-item-header">
                                  <label className="feature-checkbox">
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          updateSelectedPlan({
                                            ...selectedPlan,
                                            dynamicFeatures: [
                                              ...(selectedPlan.dynamicFeatures || []),
                                              { feature: feature._id, value: feature.defaultValue }
                                            ]
                                          });
                                        } else {
                                          updateSelectedPlan({
                                            ...selectedPlan,
                                            dynamicFeatures: (selectedPlan.dynamicFeatures || []).filter(
                                              df => (df.feature?._id || df.feature) !== feature._id
                                            )
                                          });
                                        }
                                      }}
                                    />
                                    <span className="feature-name">
                                      {isRTL ? feature.nameAr : feature.nameEn}
                                    </span>
                                  </label>
                                  <span className={`feature-type-badge ${feature.type}`}>
                                    {t(`features.types.${feature.type}`, feature.type)}
                                  </span>
                                </div>
                                
                                {isSelected && feature.type === 'limit' && (
                                  <div className="feature-value-input">
                                    <input
                                      type="number"
                                      value={currentValue ?? feature.defaultValue ?? 0}
                                      onChange={(e) => {
                                        const newValue = Number(e.target.value);
                                        updateSelectedPlan({
                                          ...selectedPlan,
                                          dynamicFeatures: (selectedPlan.dynamicFeatures || []).map(df =>
                                            (df.feature?._id || df.feature) === feature._id
                                              ? { ...df, value: newValue }
                                              : df
                                          )
                                        });
                                      }}
                                      min="-1"
                                      placeholder="-1 = غير محدود"
                                    />
                                    <span className="limit-hint">{t('subscriptions.unlimitedHint', '-1 = غير محدود')}</span>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={selectedPlan.isActive}
                    onChange={(e) => updateSelectedPlan({ ...selectedPlan, isActive: e.target.checked })}
                  />
                  {t('subscriptions.isActive')}
                </label>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={selectedPlan.isDefault || false}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      if (!checked && isEditingPlan && selectedPlan.isDefault) {
                        const confirmed = window.confirm(
                          'تحذير: إلغاء تحديد هذه الخطة كافتراضية سيلغي اشتراكات جميع المستخدمين الحاليين عليها. هل أنت متأكد؟'
                        );
                        if (!confirmed) return;
                      }
                      updateSelectedPlan({ ...selectedPlan, isDefault: checked });
                    }}
                  />
                  {t('subscriptions.isDefault', 'خطة افتراضية للمستخدمين الجدد')}
                </label>
                <small className="form-hint">{t('subscriptions.isDefaultHint', 'عند تفعيل هذا الخيار، سيتم تعيين هذه الخطة تلقائياً للمستخدمين الجدد وستصبح هي الخطة الافتراضية الوحيدة')}</small>
              </div>
            </div>
            {planModalError && (
              <div className="modal-error-banner">
                <AlertTriangle size={16} />
                <span>{planModalError}</span>
              </div>
            )}
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={handleSavePlan}>
                {isEditingPlan ? t('common.save') : t('common.create')}
              </button>
              <button className="btn btn-outline" onClick={closePlanModal}>
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Plan Modal */}
      {showUserModal && (
        <div className="modal-overlay" onClick={() => setShowUserModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('subscriptions.assignPlan', 'تعيين خطة لمستخدم')}</h2>
              <button className="modal-close" onClick={() => setShowUserModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>{t('subscriptions.selectUser', 'اختر المستخدم')}</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder={t('subscriptions.searchUser', 'ابحث عن مستخدم...')}
                  value={assignPlanData.userSearch}
                  onChange={(e) => {
                    const searchValue = e.target.value;
                    setAssignPlanData({ ...assignPlanData, userSearch: searchValue });
                    // البحث عند كل تغيير إذا كان الطول أكبر من أو يساوي 2 أو فارغ
                    if (searchValue.length >= 2 || searchValue.length === 0) {
                      fetchUsers(searchValue);
                    }
                  }}
                  onFocus={() => {
                    // جلب المستخدمين عند التركيز
                    if (users.length === 0) {
                      fetchUsers(assignPlanData.userSearch);
                    }
                  }}
                />
                {usersLoading && <Loader size={16} className="spin" />}
                {users.length > 0 && (
                  <div className="users-dropdown">
                    {users.map(user => (
                      <div
                        key={user._id}
                        className={`user-option ${assignPlanData.userId === user._id ? 'selected' : ''}`}
                        onClick={() => {
                          setAssignPlanData({
                            ...assignPlanData,
                            userId: user._id,
                            userSearch: `${user.name} (${user.email})`
                          });
                          setUsers([]);
                        }}
                      >
                        <div className="user-avatar">{user.name?.charAt(0) || '?'}</div>
                        <div className="user-info">
                          <span className="user-name">{user.name}</span>
                          <span className="user-email">{user.email}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>{t('subscriptions.selectPlan', 'اختر الخطة')}</label>
                <select
                  className="form-input"
                  value={assignPlanData.planId}
                  onChange={(e) => {
                    const planId = e.target.value;
                    const plan = plans.find(p => p._id === planId);
                    setAssignPlanData({
                      ...assignPlanData,
                      planId,
                      duration: plan?.duration ?? null
                    });
                  }}
                >
                  <option value="">{t('subscriptions.choosePlan', 'اختر خطة...')}</option>
                  {plans.map(plan => (
                    <option key={plan._id} value={plan._id}>
                      {plan.nameAr || plan.nameEn} - {plan.price === 0 ? t('subscriptions.free') : `${formatPrice(plan.price)} ${t('common.sar')}`}
                    </option>
                  ))}
                </select>
              </div>

              {assignPlanData.planId && (
                <div className="form-group">
                  <label>{t('subscriptions.customDuration', 'مدة مخصصة (اختياري)')}</label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder={t('subscriptions.durationInDays', 'المدة بالأيام')}
                    value={assignPlanData.duration ?? ''}
                    onChange={(e) => setAssignPlanData({
                      ...assignPlanData,
                      duration: e.target.value ? Number(e.target.value) : null
                    })}
                    min="0"
                  />
                  <small className="form-hint">
                    {t('subscriptions.defaultDuration', 'اتركه فارغاً لاستخدام مدة الخطة الافتراضية')}
                  </small>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={handleAssignPlan}>
                {t('subscriptions.assign', 'تعيين')}
              </button>
              <button className="btn btn-outline" onClick={() => setShowUserModal(false)}>
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Modal (Suspend/Cancel/Activate/Change Plan) */}
      {showActionModal && selectedSubscription && (
        <div className="modal-overlay" onClick={() => setShowActionModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {actionType === 'suspend' && (
                  <><Pause size={20} /> {t('subscriptions.suspendSubscription', 'إيقاف الاشتراك مؤقتاً')}</>
                )}
                {actionType === 'cancel' && (
                  <><X size={20} /> {t('subscriptions.cancelSubscription', 'إلغاء الاشتراك')}</>
                )}
                {actionType === 'activate' && (
                  <><Play size={20} /> {t('subscriptions.activateSubscription', 'تفعيل الاشتراك')}</>
                )}
                {actionType === 'change' && (
                  <><RefreshCw size={20} /> {t('subscriptions.changePlan', 'تغيير الخطة')}</>
                )}
              </h2>
              <button className="modal-close" onClick={() => setShowActionModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              {/* User info */}
              <div className="selected-user-info">
                <div className="user-avatar">
                  {(selectedSubscription.user?.name || '?').charAt(0)}
                </div>
                <div>
                  <strong>{selectedSubscription.user?.name}</strong>
                  <span>{selectedSubscription.user?.email}</span>
                </div>
              </div>

              {/* Change plan - select new plan */}
              {actionType === 'change' && (
                <div className="form-group">
                  <label>{t('subscriptions.selectNewPlan', 'اختر الخطة الجديدة')}</label>
                  <select
                    className="form-input"
                    value={newPlanId}
                    onChange={(e) => setNewPlanId(e.target.value)}
                  >
                    <option value="">{t('subscriptions.choosePlan', 'اختر خطة...')}</option>
                    {plans.filter(p => p._id !== (selectedSubscription.plan?._id || selectedSubscription.plan)).map(plan => (
                      <option key={plan._id} value={plan._id}>
                        {plan.nameAr || plan.nameEn} - {plan.price === 0 ? t('subscriptions.free') : `${formatPrice(plan.price)} ${t('common.sar')}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Duration for activate/change */}
              {(actionType === 'activate' || actionType === 'change') && (
                <div className="form-group">
                  <label>{t('subscriptions.newDuration', 'المدة الجديدة (اختياري)')}</label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder={t('subscriptions.durationInDays', 'المدة بالأيام')}
                    value={newDuration}
                    onChange={(e) => setNewDuration(e.target.value)}
                    min="0"
                  />
                  <small className="form-hint">
                    {t('subscriptions.defaultDuration', 'اتركه فارغاً لاستخدام مدة الخطة الافتراضية')}
                  </small>
                </div>
              )}

              {/* Reason */}
              <div className="form-group">
                <label>{t('subscriptions.reason', 'السبب / ملاحظات')}</label>
                <textarea
                  className="form-input"
                  rows={3}
                  value={actionReason}
                  onChange={(e) => setActionReason(e.target.value)}
                  placeholder={t('subscriptions.enterReason', 'أدخل السبب أو ملاحظات...')}
                />
              </div>

              {/* Warning for cancel */}
              {actionType === 'cancel' && (
                <div className="warning-box">
                  <AlertTriangle size={20} />
                  <span>{t('subscriptions.cancelWarning', 'سيتم إلغاء الاشتراك بشكل نهائي ولن يتمكن المستخدم من الوصول للميزات')}</span>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button 
                className={`btn ${actionType === 'cancel' ? 'btn-danger' : actionType === 'suspend' ? 'btn-warning' : 'btn-primary'}`}
                onClick={handleSubscriptionAction}
              >
                {actionType === 'suspend' && t('subscriptions.suspend', 'إيقاف')}
                {actionType === 'cancel' && t('subscriptions.cancel', 'إلغاء')}
                {actionType === 'activate' && t('subscriptions.activate', 'تفعيل')}
                {actionType === 'change' && t('subscriptions.changePlan', 'تغيير')}
              </button>
              <button className="btn btn-outline" onClick={() => setShowActionModal(false)}>
                {t('common.close', 'إغلاق')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Logs Modal */}
      {showLogsModal && selectedSubscription && (
        <div className="modal-overlay" onClick={() => setShowLogsModal(false)}>
          <div className="modal-content modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <History size={20} />
                {t('subscriptions.subscriptionHistory', 'سجل الاشتراك')}
              </h2>
              <button className="modal-close" onClick={() => setShowLogsModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              {/* User info */}
              <div className="selected-user-info">
                <div className="user-avatar">
                  {(selectedSubscription.user?.name || '?').charAt(0)}
                </div>
                <div>
                  <strong>{selectedSubscription.user?.name}</strong>
                  <span>{selectedSubscription.user?.email}</span>
                </div>
              </div>

              {/* Logs list */}
              {logsLoading ? (
                <div className="loading-state">
                  <Loader size={24} className="spin" />
                </div>
              ) : logs.length > 0 ? (
                <div className="logs-timeline">
                  {logs.map((log, index) => (
                    <div key={log._id || index} className="log-item">
                      <div className="log-icon">
                        {log.action === 'created' && <Plus size={16} />}
                        {log.action === 'activated' && <Play size={16} />}
                        {log.action === 'suspended' && <Pause size={16} />}
                        {log.action === 'cancelled' && <X size={16} />}
                        {log.action === 'upgraded' && <Crown size={16} />}
                        {log.action === 'downgraded' && <Package size={16} />}
                        {log.action === 'plan_changed' && <RefreshCw size={16} />}
                        {log.action === 'admin_granted' && <Star size={16} />}
                        {!['created', 'activated', 'suspended', 'cancelled', 'upgraded', 'downgraded', 'plan_changed', 'admin_granted'].includes(log.action) && <Clock size={16} />}
                      </div>
                      <div className="log-content">
                        <div className="log-header">
                          <span className="log-action">{getActionLabel(log.action)}</span>
                          <span className="log-date">{formatDate(log.createdAt)}</span>
                        </div>
                        {log.description && <p className="log-description">{log.description}</p>}
                        {log.admin && (
                          <span className="log-admin">
                            بواسطة: {log.admin.name || log.admin.email}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-logs">
                  <History size={48} />
                  <p>{t('subscriptions.noLogs', 'لا يوجد سجل')}</p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowLogsModal(false)}>
                {t('common.close', 'إغلاق')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function for action labels
const getActionLabel = (action) => {
  const labels = {
    created: 'تم إنشاء الاشتراك',
    activated: 'تم تفعيل الاشتراك',
    suspended: 'تم إيقاف الاشتراك',
    cancelled: 'تم إلغاء الاشتراك',
    expired: 'انتهت صلاحية الاشتراك',
    renewed: 'تم تجديد الاشتراك',
    upgraded: 'تمت ترقية الخطة',
    downgraded: 'تم تخفيض الخطة',
    plan_changed: 'تم تغيير الخطة',
    trial_started: 'بدأت التجربة المجانية',
    trial_ended: 'انتهت التجربة المجانية',
    payment_received: 'تم استلام الدفع',
    payment_failed: 'فشل الدفع',
    auto_renewed: 'تجديد تلقائي',
    admin_granted: 'تم منح الخطة من الأدمن',
    admin_revoked: 'تم سحب الخطة من الأدمن'
  };
  return labels[action] || action;
};

export default SubscriptionsPage;
